import colorsUtils from '@/utils/colors.utils.ts'

/**
 * Creates a smooth vertical gradient for filling under a line chart.
 *
 * The gradient starts near the line (top) with a stronger opacity and
 * gradually fades to full transparency toward the bottom of the chart.
 * Useful for generating modern, soft area-under-line fills.
 *
 * @param ctx        Canvas 2D rendering context used to create the gradient.
 * @param top        Y-coordinate (in pixels) where the gradient begins.
 * @param bottom     Y-coordinate (in pixels) where the gradient ends.
 * @param baseColor  Base color of the dataset (hex string, e.g. "#4CAF50").
 *
 * @returns {CanvasGradient} A vertical gradient fading from `baseColor`
 *                           (semi-opaque) at the top to transparent at the bottom.
 *
 * @example
 * const gradient = makeVerticalGradient(ctx, 0, chart.height, '#2196F3')
 * ctx.fillStyle = gradient
 * ctx.fill()
 */
const makeVerticalGradient = (ctx: CanvasRenderingContext2D, top: number, bottom: number, baseColor: string) => {
  const g = ctx.createLinearGradient(0, top, 0, bottom)
  // Stronger near the line, quick falloff, very soft tail — tweak to taste
  g.addColorStop(0.0, colorsUtils.hexToRgba(baseColor, 0.35))
  g.addColorStop(0.15, colorsUtils.hexToRgba(baseColor, 0.18))
  g.addColorStop(0.35, colorsUtils.hexToRgba(baseColor, 0.08))
  g.addColorStop(1.0, colorsUtils.hexToRgba(baseColor, 0.0))
  return g
}

const canvasGradients = { makeVerticalGradient }
export default canvasGradients
